
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from 'src/services/auth.service';
import { ScormService, SCORM_SESSION_STORAGE_KEY } from 'src/services/scorm.service';
import { ContenuSessionService } from 'src/services/contenu.session.service';
import { UtilisateurService } from 'src/services/utilisateur.service';

@Component({
    selector: 'app-scorm-viewer',
    templateUrl: './scorm-viewer.component.html',
    styleUrls: ['./scorm-viewer.component.scss'],
    imports: []
})
export class ScormViewerComponent implements OnInit, OnDestroy {
  private static readonly TOKEN_REFRESH_SKEW_SECONDS = 60;
  scormId!: number;
  formationGroupeId?: number | null;
  scormUrl!: SafeResourceUrl;
  private langChangeSubscription?: Subscription;
  private previewSeekInterval?: ReturnType<typeof setInterval>;
  private quitting = false;
  private tokenRefreshPromise: Promise<string | null> | null = null;
  private readonly quitMessageHandler = (event: MessageEvent) => this.onScormMessage(event);

  constructor(
    private scormService: ScormService,
    private authService: AuthService,
    private utilisateurService: UtilisateurService,
    private contenuSessionService: ContenuSessionService,
    private sanitizer: DomSanitizer,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private ngZone: NgZone,
    private router: Router
  ) { }

  ngOnInit() {
    if (!this.utilisateurService.entiteUtilisateurSelectionnee || !this.authService.User) {
      this.router.navigate(['accueil']);
      return;
    }

    // Le contenu SCORM (bouton « Quitter ») demande la fermeture via postMessage.
    window.addEventListener('message', this.quitMessageHandler);

    this.scormId = Number(this.activatedRoute.snapshot.paramMap.get("scormContentId"));
    if (this.activatedRoute.snapshot.paramMap.get("formationGroupeId") != null) {
      this.formationGroupeId = Number(this.activatedRoute.snapshot.paramMap.get("formationGroupeId"));
    }

    const previewMode = this.activatedRoute.snapshot.queryParamMap.get("preview");
    if (previewMode === 'true') {
      window.previewMode = true;
    } else {
      window.previewMode = false;
    }

    window.scormId = this.scormId;
    window.ssoId = this.authService.User?.profile.sub;
    window.entiteId = this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
    window.utilisateurId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;
    window.formationGroupeId = this.formationGroupeId;
    window.currentLanguage = this.translate.currentLang || 'fr';
    window.learnerName = `${this.authService.User?.profile.given_name} ${this.authService.User?.profile.family_name}` || 'Anonyme';

    window.getAccessToken = () => this.resolveAccessToken();
    window.refreshAccessToken = () => this.resolveAccessToken(true);

    this.langChangeSubscription = this.translate.onLangChange.subscribe(event => {
      window.currentLanguage = event.lang;
    });

    this.rejouerEvenementsEnAttente();

    this.ensureSession().then(ready => {
      if (!ready) {
        return;
      }
      this.applyLanguageToSession();
      this.loadScormContent();
    });
  }

  private async ensureSession(): Promise<boolean> {
    if (window.previewMode === true || (window.sessionId ?? 0) > 0) {
      return true;
    }

    const stored = this.readStoredSession();
    if (stored && stored.scormId === this.scormId && stored.sessionId > 0) {
      try {
        const evenements = await firstValueFrom(this.contenuSessionService.obtenirDonneesSession(stored.sessionId));
        window.dataSession = evenements;
        window.sessionId = stored.sessionId;
        return true;
      } catch (error) {
        console.warn('Reprise de session échouée, redirection vers accueil', error);
      }
    }

    this.router.navigate(['accueil']);
    return false;
  }

  private rejouerEvenementsEnAttente(): void {
    if (typeof window.rejouerEvenementsEnAttente === 'function') {
      window.rejouerEvenementsEnAttente();
    }
  }

  private readStoredSession(): { scormId: number; sessionId: number } | null {
    try {
      const raw = sessionStorage.getItem(SCORM_SESSION_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private applyLanguageToSession(): void {
    if (window.dataSession && Array.isArray(window.dataSession)) {
      const languageEntry = window.dataSession.find((entry: any) =>
        entry.typeEvenement === 'cmi.learner_preference.language'
      );

      if (languageEntry) {
        languageEntry.valeur = window.currentLanguage;
      }
    }
  }

  private loadScormContent(): void {
    this.scormService.getScormContent(this.scormId).subscribe(url => {
      const config = (window as any).SCORM_API_CONFIG;
      if (config && config.USE_POST_MESSAGE_BRIDGE && config.BRIDGE_URL) {
        // Bridge mode: store the actual content URL for the PostMessage handler,
        // and load the bridge page instead
        window.scormContentUrl = url;
        this.scormUrl = this.sanitizer.bypassSecurityTrustResourceUrl(config.BRIDGE_URL);
      } else {
        // Same-origin mode: load SCORM content directly
        this.scormUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }
    });
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.quitMessageHandler);
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  private resolveAccessToken(forceRefresh: boolean = false): Promise<string | null> {
    const user = this.authService.User;
    const secondsRemaining = user?.expires_in;

    if (!forceRefresh
      && user?.access_token
      && secondsRemaining != null
      && secondsRemaining > ScormViewerComponent.TOKEN_REFRESH_SKEW_SECONDS) {
      return Promise.resolve(user.access_token);
    }

    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = firstValueFrom(this.authService.refreshToken())
      .catch(() => forceRefresh ? null : (this.authService.User?.access_token ?? null))
      .finally(() => { this.tokenRefreshPromise = null; });

    return this.tokenRefreshPromise;
  }

  onIframeLoad(iframe: HTMLIFrameElement): void {
    const config = (window as any).SCORM_API_CONFIG;
    if (config && config.USE_POST_MESSAGE_BRIDGE) {
      return;
    }

    try {
      const contentWindow = iframe.contentWindow;
      if (!contentWindow) {
        return;
      }

      contentWindow.close = () => this.navigateToAccueil();
    } catch (error) {
      console.warn('Impossible de rediriger la fermeture du contenu SCORM vers accueil', error);
    }
  }

  private onScormMessage(event: MessageEvent): void {
    if (event?.data?.type !== 'scorm_quit') {
      return;
    }

    this.navigateToAccueil();
  }

  private navigateToAccueil(): void {
    if (this.quitting) {
      return;
    }
    this.quitting = true;

    this.ngZone.run(() => this.router.navigate(['accueil']));
  }
}
